import { ActivatedRoute } from "@angular/router";
import { BreadcrumbLevel, Breadcrumbs } from "@carm/shared/constants/const-bread-crumbs";
import { CarmRouter } from "@carm/_core/carm-router/services/carm-router.service";
import { FinancialSecurityRequestSource } from "@carm/_core/financial-security/models/financial-security.model";
import { IErrorResponse } from "@carm/_utils/models";
import { TranslateService } from "@ngx-translate/core";

export enum CallStatus {
    Obtained,
    Empty,
    NotCalled,
    Error,
}

export enum MonerisPaymentStatus {
  Approved = 'APPROVED',
  Cancelled = 'CANCELLED',
  Declined = 'DECLINED'
}

export const RETURN_URL_STORAGE_KEY = 'returnUrl';
export const QUERY_PARAMS_STORAGE_KEY = 'queryParams';
export const ENROLLMENT_DRAFT_ID = 'draft';
export const PAYMENT_CONTEXT = 'payment-context';
export const LAST_NB = 'native-business';
export const LAST_PROGRAM = 'program';
export const MONERIS_GENERIC_EXCEPTION_CODE = 'carm.moneris.generic.exception';


export interface MonerisPreloadResponse {
    hpp_id?: string;
    order_id?: string;
    ticket?: string;
}

export interface ICarmMonerisResponse {
  orderId?: string;
  bankTransactionId?: string;
  authCode?: string;
  transactionType?: string;
  dataStamp?: string;
  chargeAmount?: number;
  cardType?: string;
  cardInfo?: string;
  responseCode?: number;
  status?: string;
}

export interface MonerisResponse extends IErrorResponse {
  amount?: number;
  cardNumber?: string;
  cardType?: string;
  responseCode?: string;
  status?: MonerisPaymentStatus;
  transactionDateTime?: string;
  transactionKey?: string;
  txnNumber?: string;
  uid?: string;
}

export const mapFromMonerisToCarmResponse = (monerisResponse: MonerisResponse, translateService: TranslateService): ICarmMonerisResponse => {
  return {
    chargeAmount: monerisResponse?.amount,
    transactionType: translateService.instant('moneris.purchase'),
    dataStamp: monerisResponse?.transactionDateTime,
    orderId: monerisResponse?.uid,
    cardInfo: monerisResponse?.cardNumber,
    cardType: mapCardType(monerisResponse?.cardType),
    bankTransactionId: monerisResponse?.transactionKey,
    responseCode: +monerisResponse?.responseCode,
    authCode: monerisResponse?.txnNumber,
    status: monerisResponse?.status
  } as ICarmMonerisResponse;
};

export const navigateToInitiatedPaymentPage = (carmRouter: CarmRouter): void => {
  let route = localStorage.getItem(RETURN_URL_STORAGE_KEY);
  const hasQueryParams = route.includes('?');
  if (hasQueryParams) {
    route = route.substr(0, route.indexOf('?'));
  }
  const queryParams = JSON.parse(localStorage.getItem(QUERY_PARAMS_STORAGE_KEY));
  carmRouter.carmNavigate([route], { queryParams, queryParamsHandling: 'merge' });
};

export const encodeAmount = (amount: string): string => {
  return encodeURIComponent(btoa((+amount*1000000).toString()));
};

export const decodeAmount = (encodedAmount: string): string => {
  return (+atob(decodeURIComponent(encodedAmount))/1000000).toFixed(2).toString();
};

export const getBreadcrumb = (router: CarmRouter, route: ActivatedRoute): BreadcrumbLevel[] => {
  const notOrphan = !!router.bn9;
  const lastQueryParamsStored = localStorage.getItem('queryParams');
  const lastQueryParamsStoredParsed =  !!lastQueryParamsStored ? JSON.parse(lastQueryParamsStored) : null;
  const requestSource = route.snapshot?.queryParams?.requestSource ?? lastQueryParamsStoredParsed?.requestSource;

  if (notOrphan && !!requestSource) {
    switch (requestSource) {
      case FinancialSecurityRequestSource.Payments:
        return Breadcrumbs.Payments_Main;
      case FinancialSecurityRequestSource.FinancialSecurityDashboard:
        return Breadcrumbs.Financial_FinancialSecurity;
      case FinancialSecurityRequestSource.TransactionHistory:
      case FinancialSecurityRequestSource.ClientTransactionHistory:
        return Breadcrumbs.Financial_TransactionHistory;
      case FinancialSecurityRequestSource.ProgramEnrolmentAddBonds:
      case FinancialSecurityRequestSource.ProgramEnrolmentPendingEnrolments:
        return Breadcrumbs.AccountsProfilesMgt_ProgramAccountEnrolment;
      case FinancialSecurityRequestSource.SubprogramRequests:
        return Breadcrumbs.AccountsProfilesMgt_ProgramAccountProfileSubPrograms;
    }
  } else {
    return Breadcrumbs.Payments_Main;
  }
};

const mapCardType = (cardType: string): string => {
  switch (cardType) {
    case 'V':
      return 'VISA';
    case 'M':
      return 'MC';
    case 'D':
    case 'P':
      return 'DEBIT';
    case 'AX':
      return 'AMEX';
    default:
      return cardType;
  }
};
